import React from 'react';
import { useTranslation } from 'react-i18next';

import FlexRow from '@/components/Common/FlexRow';

import RoomScreenTime from './RoomScreenTime';
import StatisticsPanel from './StatisticsPanel';

export interface RoomStatisticsProps {
  projectId:			string;
  roomNo:				string;
  // Whether the room's RESOLVED screen-time settings are active — passed
  // straight through to RoomScreenTime's empty-state messaging.
  configured:			boolean;
  // Fired with false only once BOTH panels below report themselves
  // unavailable, so the parent drops the tab only when neither half has
  // anything to show — one working panel is reason enough to keep the tab.
  onAvailability?:		(available: boolean) => void;
  onOpenSettings?:		() => void;
  // Erasing screen-time history follows the `enable-screentime` ACL alone,
  // same as before — unrelated to the new `enable-statistics` tab gate.
  canReset?:			boolean;
}

// Room-level Statistics tab: the existing per-minute screen-time timeline
// (RoomScreenTime, unchanged) stacked above the room-scoped app-usage panel,
// split by a divider line so the two features never read as one blended
// figure. The app-usage panel is pinned to this room (`fixedRoomNo`) — no
// room picker, no rooms ranking (nothing to rank against), and its own
// embedded screen-time summary is turned off since the detailed timeline
// above already covers it in full.
const RoomStatistics = (props: RoomStatisticsProps) => {
  const { t } = useTranslation();

  const [screenTimeAvailable, setScreenTimeAvailable]	= React.useState(true);
  const [appUsageAvailable, setAppUsageAvailable]		= React.useState(true);

  const onAvailability = props.onAvailability;

  React.useEffect(() => {
    if (onAvailability)
      onAvailability(screenTimeAvailable || appUsageAvailable);
  }, [screenTimeAvailable, appUsageAvailable, onAvailability]);

  return (
  <FlexRow direction="column"
           align="start"
           className="w-full gap-[16px]">
    <RoomScreenTime projectId={props.projectId}
                    roomNo={props.roomNo}
                    configured={props.configured}
                    canReset={props.canReset}
                    onAvailability={setScreenTimeAvailable}
                    onOpenSettings={props.onOpenSettings} />

    <div className="w-full max-w-[1100px] h-[1px] bg-[#E7E9EF]" />

    <StatisticsPanel projectId={props.projectId}
                      fixedRoomNo={props.roomNo}
                      showScreentimeCard={false}
                      subtitle={t('projects:project:statistics:subtitle_room')}
                      onAvailability={setAppUsageAvailable} />
  </FlexRow>
  );
};

export default RoomStatistics;
